import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import request from "../../../utilities/requests";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoDialog from "./InfoDialog";
import ConfirmRedirectDialog from "./ConfirmRedirectDialog";
import MessageDialog from "../../../components/MessageDialog";
import Notebook from "./Notebook";
import RefundDialog from "./RefundDialog";
import DealBreakersDialog from "./DealBreakersDialog";
import OrdersDialog from "../../../components/OrdersDialog";
import ProfileTour from "../../Profile/components/ProfileTour";
import { AppContext } from "../../../contexts/AppContext";
import AuthContext from "../../../contexts/AuthContext";

import { toast } from "../../../utilities/SnackBarUtils";
import { getWalkupTourPending, clearWalkupTourPending } from "../../../utilities/walkup";

import "../events.css";

const MyEvents = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { setActiveEvent, myEvents, setMyEvents, event } =
    useContext(AppContext);
  const { socket, setPromptData, listeningForPrompt, setListeningForPrompt } =
    useContext(AuthContext);
  const [loaded, setLoaded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [chatUnread, setChatUnread] = useState({});
  const [activeEvents, setActiveEvents] = useState([]);
  const [completedEvents, setCompletedEvents] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [open, setOpen] = useState(false);
  const [eventUrl, setEventUrl] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({
    open: false,
    title: "",
    body: "",
    checkInMsg: false,
    page: null,
    eventId: null,
  });
  const [checkingIn, setCheckingIn] = useState(false);
  const [notebookOpen, setNotebookOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventCounts, setEventCounts] = useState({
    active: 0,
    completed: 0,
  });
  const [refundData, setRefundData] = useState({});
  const [refundOpen, setRefundOpen] = useState(false);
  const [dealBreakersOpen, setDealBreakersOpen] = useState(false);
  // Walk-in tour: highlights the right action on a walk-up-enabled, in-progress
  // event card (check-in if profile is done, else the complete-profile button).
  const [walkInTour, setWalkInTour] = useState(null); // { eventId, phase }

  useEffect(() => {
    if (!loaded) return;
    // The walk-in tour only shows as part of a fresh walk-up journey (armed at
    // registration for first-timers). Returning scanners never have this set.
    if (!getWalkupTourPending()) return;
    const candidate = activeEvents.find(
      (e) =>
        e.allowWalkUpRegistration &&
        ["pending", "active"].includes(e.status) &&
        !e.checkedIn,
    );
    if (!candidate) return;
    const phase = candidate.checkIn && !candidate.checkIn.eligible ? "profile" : "checkin";
    try {
      if (localStorage.getItem(`walkinTourSeen_${candidate._id}_${phase}`) === "true") return;
    } catch (e) {
      /* ignore */
    }
    setWalkInTour({ eventId: candidate._id, phase });
  }, [loaded, activeEvents]);

  const walkInSteps = useMemo(() => {
    if (!walkInTour) return [];
    if (walkInTour.phase === "profile") {
      return [
        {
          selector: '[data-tour="walkin-profile"]',
          title: t("walkin.tour.profile.title"),
          body: t("walkin.tour.profile.body"),
        },
      ];
    }
    return [
      {
        selector: '[data-tour="walkin-checkin"]',
        title: t("walkin.tour.checkin.title"),
        body: t("walkin.tour.checkin.body"),
      },
    ];
  }, [walkInTour, t]);

  const finishWalkInTour = () => {
    if (walkInTour) {
      try {
        localStorage.setItem(`walkinTourSeen_${walkInTour.eventId}_${walkInTour.phase}`, "true");
      } catch (e) {
        /* ignore */
      }
      // The check-in tour is the end of the journey - retire the one-shot tour
      // flag so it never shows again.
      if (walkInTour.phase === "checkin") clearWalkupTourPending();
    }
    setWalkInTour(null);
  };

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/myevents", title: "MyEvents" });
  }, []);

  useEffect(() => {
    const primeData = async () => {
      await getMyEvents();
      getActiveEvent();
      setLoaded(true);
    };
    primeData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getMyEvents = async () => {
    console.log("retrieving my events");
    const result = await request("events?registered=true");

    if (result.success) {
      setMyEvents([...result.events]);
      const now = new Date();
      const isCompleted = (e) => {
        if (e.eventType === "League") {
          return e.end && new Date(e.end) < now;
        }
        return e.status === "completed";
      };
      const ce = result.events.filter(isCompleted);
      setCompletedEvents(ce);
      const ae = result.events.filter((e) => !isCompleted(e));
      setActiveEvents(ae);
      loadChatUnread(ae);
    }
  };

  const loadChatUnread = async (events) => {
    const ids = events
      .filter((e) => e.eventType === "League" && e.leagueEventId)
      .map((e) => e.leagueEventId);
    if (ids.length === 0) return;

    const result = await request(
      `leaguechat/unread?leagueEventIds=${ids.join(",")}`
    );
    if (result?.success) setChatUnread(result.counts || {});
  };

  const getActiveEvent = async () => {
    const result = await request("events/activeevent");
    if (!result.success) {
      toast.error(result.error);
      navigate("/events");
    }
    setActiveEvent(result.event);
  };

  const handleClose = () => {
    setOpen(false);
    setEventUrl(null);
  };

  const handleCloseInfo = () => {
    setInfoOpen(false);
    setSelectedEvent(null);
  };

  const checkForRefund = async (event) => {
    const result = await request(`ticketing/orders/?eventId=${event._id}`);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    if (result?.redirectToEventbrite) {
      setOpen(true);
      setEventUrl(result.redirectUrl);
    } else {
      setRefundData({ orders: result?.data?.orders, event });
      setRefundOpen(true);
    }
  };

  const handleOpenLink = () => {
    window.open(eventUrl, "_blank", "noreferrer");
    setOpen(false);
    setEventUrl(null);
  };

  const handleCheckIn = async (event) => {
    setCheckingIn(true);
    if (event?.pairingEventType === "dealbreakers") {
      setDealBreakersOpen(true);
      setCheckingIn(false);
      return;
    }

    const result = await request("events/checkin", "post", {
      eventId: event._id,
    });
    setCheckingIn(false);
    if (!result?.success && result?.incompleteProfile) {
      setDialogInfo({
        ...dialogInfo,
        open: true,
        title: t("checkin.completeprofile.title"),
        body: result.message,
        page: "profile",
        eventId: null,
      });
      return;
    } else if (!result.success) {
      setDialogInfo({
        ...dialogInfo,
        open: true,
        title: t("checkin.error.title"),
        body: result.error,
        page: "profile",
        eventId: null,
      });
      return;
    }
    setDialogInfo({
      ...dialogInfo,
      open: true,
      title: t("checkin.success.title"),
      body: t("checkin.success.content"),
      checkInMsg: true,
      eventId: event?._id,
    });
    await getMyEvents();
    setCheckInOpen(true);
    getActiveEvent();
  };

  const handleCheckInClose = async () => {
    if (dialogInfo.checkInMsg) {
      try {
        const body = {
          eventId: dialogInfo?.eventId,
          trigger: "afterCheckin",
        };
        const result = await request("ads/ad/event", "post", body);
        console.log(result);
      } catch (e) {
        var error = `Error triggering ad check. Error: ${e.message}`;
        console.error(error);
      }
    }
    setDialogInfo({
      ...dialogInfo,
      open: false,
      title: "",
      body: "",
      checkInMsg: false,
      page: null,
      eventId: null,
    });
  };

  const formatDate = (date) => {
    let d = new Date(date);
    let dateString = d.toDateString();
    let hours = d.getHours();
    let minutes = d.getMinutes();

    let timeString;
    if (hours > 12 && minutes === 0) {
      timeString = `${hours - 12} PM`;
    } else if (hours > 12 && minutes > 0) {
      timeString = `${hours - 12}:${minutes} PM`;
    } else if (hours <= 12 && minutes === 0) {
      timeString = `${hours} AM`;
    } else if (hours <= 12 && minutes > 0) {
      timeString = `${hours}:${minutes} AM`;
    }

    return `${dateString} @ ${timeString}`;
  };

  const renderCardContent = (eventData) => {
    if (eventData.eventType === "League") {
      const startStr = eventData.start
        ? new Date(eventData.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : null;
      const endStr = eventData.end
        ? new Date(eventData.end).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : null;
      const dateRange = startStr && endStr
        ? `${startStr} – ${endStr}`
        : startStr || endStr || "";
      return (
        <>
          {dateRange && (
            <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
              {dateRange}
            </Typography>
          )}
          {eventData.sportType && (
            <Typography variant="body2" color="text.secondary">
              {eventData.sportType}
            </Typography>
          )}
        </>
      );
    }

    if (eventData.status === "completed") {
      return (
        <>
          <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
            {formatDate(eventData.date)}
          </Typography>
          <Typography align="center" style={{ marginTop: 10 }}>
            Event Completed
          </Typography>
        </>
      );
    }
    if (!eventData.checkedIn) {
      return (
        <>
          <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
            {formatDate(eventData.date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {eventData.description}
          </Typography>
        </>
      );
    } else if (eventData.checkedIn && eventData.status !== "active") {
      return (
        <>
          <Typography variant="subtitle2" style={{ color: "#6e0101" }}>
            {formatDate(eventData.date)}
          </Typography>
          {/* <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div> */}
        </>
      );
    } else if (eventData.checkedIn && eventData.status === "active") {
      // if (eventData?._id !== event?._id) {
      //   console.log("refresh active event");
      //   getActiveEvent();
      // }
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CheckCircleOutlineIcon className="event-ready-chk" />
        </div>
      );
    }
  };

  const renderGuestListBtn = (event) => {
    if (event?.showGuestList && event?.pairingEventType !== "mixer") {
      return (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              navigate(`/activeevent/${event?._id}`, {
                state: { page: "guestList" },
              })
            }
            fullWidth
          >
            View Guestlist
          </Button>
        </Grid>
      );
    }

    return null;
  };

  const renderPitchesBtn = (event) => {
    if (event?.pitches && event?.pitches?.enabled) {
      let btn = "View Pitches";
      if (event?.pitches?.voting) {
        btn = "Vote";
      }
      return (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/vote/${event?._id}`)}
            fullWidth
          >
            {btn}
          </Button>
        </Grid>
      );
    }

    return null;
  };

  const buildMissingItems = (checkIn) => {
    if (!checkIn?.reasons?.length) return [];
    const items = [];
    if (checkIn.reasons.includes("incompleteProfile")) {
      items.push(
        `Unanswered questions in your ${checkIn.questionGroup || "event"} profile`,
      );
    }
    if (checkIn.reasons.includes("nameRequired")) {
      items.push("Your first and last name");
    }
    if (checkIn.reasons.includes("photoRequired")) {
      items.push("A profile picture");
    }
    return items;
  };

  const renderButtons = (event) => {
    // League events get a single "Go to League" button
    if (event.eventType === "League") {
      const unread = chatUnread[event.leagueEventId] || 0;
      return (
        <Grid item xs={12}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              ReactGA.event({
                category: "league",
                action: "goto_league",
                label: event.name,
              });
              navigate(`/league/${event._id}`);
            }}
            fullWidth
          >
            Go to League
          </Button>
          {unread > 0 && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <Badge color="error" badgeContent={unread > 99 ? "99+" : unread} />
              <span style={{ marginLeft: 10 }}>
                {unread === 1 ? "new message" : "new messages"} in league chat
              </span>
            </Typography>
          )}
        </Grid>
      );
    }

    if (event.status === "scheduled") {
      return (
        <Grid
          item
          xs={6}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            onClick={() => {
              setSelectedEvent(event);
              setInfoOpen(true);
            }}
          >
            {t("myevents.info.button")}
          </Button>
        </Grid>
      );
    } else if (
      ["pending", "active"].indexOf(event.status) > -1 &&
      !event.checkedIn
    ) {
      if (event.checkInDisabled) {
        return (
          <Grid item xs={12}>
            <Alert severity="info" variant="outlined" style={{ textAlign: "left" }}>
              <Typography variant="body2">
                Check in is no longer available. Please check in with your event
                host.
              </Typography>
            </Alert>
          </Grid>
        );
      }
      if (event.checkIn && !event.checkIn.eligible) {
        return (
          <Grid
            item
            xs={12}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <Alert severity="warning" variant="outlined" style={{ textAlign: "left" }}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                You're almost there! Just finish these to check in:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {buildMissingItems(event.checkIn).map((item) => (
                  <li key={item}>
                    <Typography variant="body2" component="span">
                      {item}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Alert>
            <Button
              variant="contained"
              size="small"
              data-tour={
                walkInTour?.eventId === event._id && walkInTour?.phase === "profile"
                  ? "walkin-profile"
                  : undefined
              }
              onClick={() =>
                navigate("/profile", {
                  state: {
                    profileTour: {
                      questionGroupId: event.checkIn.reasons.includes(
                        "incompleteProfile",
                      )
                        ? event.checkIn.questionGroupId
                        : null,
                      questionGroupName: event.checkIn.questionGroup,
                      name: event.checkIn.reasons.includes("nameRequired"),
                      photo: event.checkIn.reasons.includes("photoRequired"),
                    },
                  },
                })
              }
              fullWidth
            >
              {t("myevents.completeprofile.button", "Complete Profile")}
            </Button>
          </Grid>
        );
      }
      return (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            data-tour={
              walkInTour?.eventId === event._id && walkInTour?.phase === "checkin"
                ? "walkin-checkin"
                : undefined
            }
            onClick={() => handleCheckIn(event)}
            disabled={event.checkedIn ? true : false}
            fullWidth
          >
            {checkingIn ? (
              <CircularProgress size={20} />
            ) : (
              t("myevents.checkin.button")
            )}
          </Button>
        </Grid>
      );
    } else if (event.status === "pending" && event.checkedIn) {
      return (
        <Grid item xs={12}>
          <Button
            // variant="outlined"
            size="small"
            fullWidth
          >
            {t("myevents.startingsoon.label")}
          </Button>
        </Grid>
      );
    } else if (event.status === "active" && event.checkedIn) {
      // setActiveEvent(event);
      let page;
      if (["mixer", "dinner"].includes(event?.pairingEventType)) {
        page = "mixer";
      } else {
        page = "speedDating";
      }

      return (
        <>
          <Grid item xs={12}>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                navigate(`/activeevent/${event._id}`, { state: { page } })
              }
              fullWidth
            >
              {t("myevents.gotoevent.button")}
            </Button>
          </Grid>
          {renderGuestListBtn(event)}
          {renderPitchesBtn(event)}
        </>
      );
    } else if (event.status === "completed") {
      return (
        <Grid
          item
          xs={12}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {event?.pairingEventType !== "friend" ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(`/eventresults/${event._id}`, { state: { event } })
              }
              fullWidth
            >
              {t("myevents.viewresults.button")}
            </Button>
          ) : null}

          {event?.showNotebook ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedEventId(event._id);
                setNotebookOpen(true);
              }}
              fullWidth
            >
              {t("myevents.viewnotebook.button")}
            </Button>
          ) : null}
        </Grid>
      );
    }
  };

  const renderEvents = () => {
    if (myEvents.length === 0) {
      return (
        <Grid
          item
          style={{
            width: "80%",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <Typography align="center">
            {t("myevents.noevents.registered")}
          </Typography>
          <Typography align="center">{t("myevents.noevents.home")}</Typography>
          <Typography align="center">
            {t("myevents.noevents.contact")}
          </Typography>
        </Grid>
      );
    }

    if (
      !showCompleted &&
      activeEvents.length === 0 &&
      completedEvents.length > 0
    ) {
      return (
        <Grid
          item
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="bounce-icon">
            <ExpandLessIcon
              style={{
                width: 50,
                height: 50,
                color: theme.palette.primary.main,
              }}
            />
          </div>
          <Typography align="center">
            {t("myevents.noactiveevents.label")}
          </Typography>
        </Grid>
      );
    }

    const eventArray = showCompleted ? completedEvents : activeEvents;

    return eventArray.map((event) => {
      return (
        <Grid key={event._id} item style={{ marginBottom: 20 }}>
          <Card sx={{ width: 345 }}>
            <CardMedia
              sx={{ height: 140 }}
              image={event.imageUrl}
              title={event.name}
            />
            <CardContent>
              <Typography gutterBottom variant="h6" component="div">
                {event.name}
              </Typography>
              {renderCardContent(event)}
            </CardContent>
            {/* Inline warning for unassigned tickets */}
            {event.hasAssignableTickets && (
              <div style={{ padding: "0 12px 12px 12px" }}>
                <Alert severity="warning" variant="outlined">
                  You have unassigned tickets. Click <strong>View Order</strong>{" "}
                  to assign them.
                </Alert>
              </div>
            )}
            <Grid container spacing={2} style={{ padding: 12 }}>
              {["active", "pending", "completed"].includes(
                event.status,
              ) ? null : (
                <Grid item xs={6}>
                  <Button
                    size="small"
                    onClick={() => checkForRefund(event)}
                    disabled={event.checkedIn ? true : false}
                  >
                    {t("myevents.refundcancel.button")}
                  </Button>
                </Grid>
              )}

              {renderButtons(event)}
            </Grid>
          </Card>
        </Grid>
      );
    });
  };

  if (!loaded) {
    return (
      <div
        style={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={80} />
      </div>
    );
  }

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      // justifyContent="center"
      // spacing={3}
      style={{ marginBottom: 50, paddingBottom: 200, width: "100%" }}
    >
      <Grid item style={{ marginBottom: 20 }}>
        {completedEvents.length > 0 ? (
          <Chip
            label={t("myevents.showcompleted.button")}
            variant={showCompleted ? "" : "outlined"}
            color="primary"
            onClick={() => setShowCompleted(!showCompleted)}
          />
        ) : null}
      </Grid>
      {renderEvents()}
      <ConfirmRedirectDialog
        open={open}
        close={handleClose}
        confirm={handleOpenLink}
      />
      <InfoDialog
        open={infoOpen}
        close={handleCloseInfo}
        event={selectedEvent}
      />
      {/* <CheckInDialog open={checkInOpen} close={handleCheckInClose} /> */}
      <MessageDialog
        open={dialogInfo.open}
        close={handleCheckInClose}
        title={dialogInfo.title}
        body={dialogInfo.body}
        page={dialogInfo?.page}
      />
      <Notebook
        open={notebookOpen}
        close={() => {
          setNotebookOpen(false);
          setSelectedEventId(null);
        }}
        eventId={selectedEventId}
      />
      <OrdersDialog
        open={refundOpen}
        onClose={() => {
          getMyEvents();
          setRefundOpen(false);
          setRefundData({});
        }}
        data={refundData}
      />
      <DealBreakersDialog
        open={dealBreakersOpen}
        close={() => setDealBreakersOpen(false)}
      />
      {walkInTour && walkInSteps.length > 0 && (
        <ProfileTour steps={walkInSteps} onFinish={finishWalkInTour} />
      )}
    </Grid>
  );
};

export default MyEvents;
